import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './Checkout.css';

const Checkout = () => {
  const { courseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [discount, setDiscount] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchCourse();
  }, [courseId, user, navigate]);

  const fetchCourse = async () => {
    try {
      const response = await api.get(`/courses/${courseId}`);
      setCourse(response.data);
    } catch (error) {
      console.error('Error fetching course:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyDiscount = async () => {
    if (!discountCode.trim()) return;

    try {
      const response = await api.post('/payments/validate-discount', {
        code: discountCode,
        course_id: courseId,
      });

      if (response.data.valid) {
        setDiscount(response.data.discount);
        setError('');
      } else {
        setError(response.data.error);
        setDiscount(null);
      }
    } catch (error) {
      setError(t('checkout.invalidDiscount'));
      setDiscount(null);
    }
  };

  const handleCheckout = async () => {
    setProcessing(true);
    setError('');

    try {
      const response = await api.post('/payments/create-checkout', {
        course_id: courseId,
        discount_code: discount?.code || '',
      });

      if (response.data.free) {
        navigate(`/courses/${courseId}`);
        return;
      }

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      setError(error.response?.data?.error || t('checkout.checkoutFailed'));
      setProcessing(false);
    }
  };

  if (loading) return <div className="loading">{t('checkout.loading')}</div>;
  if (!course) return <div className="page">{t('checkout.courseNotFound')}</div>;

  const originalPrice = Number(course.sale_price || course.price || 0);
  const finalPrice = discount ? Number(discount.final_price) : originalPrice;

  return (
    <div className="page checkout-page">
      <div className="container">
        <div className="checkout-container">
          <div className="checkout-main">
            <h1>{t('checkout.title')}</h1>

            <div className="course-summary">
              <img
                src={course.thumbnail_url || '/placeholder-course.jpg'}
                alt={course.title}
              />
              <div className="course-info">
                <h2>{course.title}</h2>
                <p className="instructor">{t('checkout.by')} {course.instructor_name}</p>
                <p className="category">{course.category_name}</p>
              </div>
            </div>

            <div className="discount-section">
              <h3>{t('checkout.discountCodeTitle')}</h3>
              <div className="discount-input">
                <input
                  type="text"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value)}
                  placeholder={t('checkout.enterCode')}
                />
                <button onClick={applyDiscount} className="btn-secondary">
                  {t('checkout.apply')}
                </button>
              </div>
              {discount && (
                <div className="discount-applied">
                  {t('checkout.codeApplied')} ${discount.discount_amount.toFixed(2)}
                </div>
              )}
            </div>
          </div>

          <div className="checkout-sidebar">
            <div className="order-summary">
              <h3>{t('checkout.orderSummary')}</h3>

              <div className="price-row">
                <span>{t('checkout.originalPrice')}</span>
                <span>${parseFloat(course.price).toFixed(2)}</span>
              </div>

              {course.sale_price && course.sale_price < course.price && (
                <div className="price-row sale">
                  <span>{t('checkout.saleDiscount')}</span>
                  <span>-${(course.price - course.sale_price).toFixed(2)}</span>
                </div>
              )}

              {discount && (
                <div className="price-row discount">
                  <span>{t('checkout.couponDiscount')}</span>
                  <span>-${discount.discount_amount.toFixed(2)}</span>
                </div>
              )}

              <div className="price-row total">
                <span>{t('checkout.total')}</span>
                <span>${finalPrice.toFixed(2)}</span>
              </div>

              {error && <div className="error">{error}</div>}

              <button
                onClick={handleCheckout}
                disabled={processing}
                className="btn-primary checkout-btn"
              >
                {processing ? t('checkout.processing') : finalPrice === 0 ? t('checkout.enrollFree') : t('checkout.proceedPayment')}
              </button>

              <p className="secure-note">{t('checkout.secureNote')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
